/**
 * Descriptor quote (Phase 1, plan §5 "Client quote").
 *
 * The client half of the ONE price function: for a SKU in DESCRIPTOR_PRICED the badge and
 * the selection toolbar (both via computeNodeBaseCredits) quote `priceSku()` — the SAME
 * function CreditService.calculateCost charges with — times the admin override multiplier
 * the server published on /api/credits/pricing. For every other SKU it returns undefined and
 * the legacy client mirrors keep quoting, unchanged.
 *
 * Imports only the pure descriptor modules (engine / catalog) — no server code.
 */
import type { PriceAxes } from '../../server/config/models/types';
import { DESCRIPTOR_PRICED, skuKey } from '../../server/config/models/catalog';
import { priceSku } from '../../server/config/models/engine';
import { getPriceOverrideMultiplier } from './creditCostUtils';

/** Whether this fal.ai SKU is priced by its descriptor (code-gated set, empty until a price flip). */
export function isDescriptorPriced(skuId: string, priced: ReadonlySet<string> = DESCRIPTOR_PRICED): boolean {
  return priced.has(skuKey('falai', skuId));
}

/**
 * Credits for one generation of a descriptor-priced fal.ai SKU, or undefined when the SKU is
 * not descriptor-priced (the caller then uses its legacy branch).
 */
export function descriptorQuote(
  skuId: string,
  axes: PriceAxes,
  opts: { priced?: ReadonlySet<string>; price?: typeof priceSku } = {},
): number | undefined {
  if (!isDescriptorPriced(skuId, opts.priced)) return undefined;
  return (opts.price ?? priceSku)(skuId, axes, getPriceOverrideMultiplier('falai', skuId));
}
